import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronUp, RotateCcw, SlidersHorizontal, X } from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { DEFAULT_TUBE_COLORS, DEFAULT_TUBE_OPACITY } from './TubesBackground';

/* My board (2026-10-24). J.O.: "an empty page at the bottom of Milli, with a
 * little hide-away side bar to change the colors, where I just pick the
 * cards I'll be using."
 *
 * Open space (2026-10-25). J.O.: "an actual open space to be able to see
 * the ribbons moving using the mouse." The board is clear by default now:
 * no ground, no frame, a tall stretch of page with nothing over the tube
 * tracers, so the ribbons show through at full strength and follow the
 * mouse. Cards he picks float on it. The side bar gained a Ribbons section:
 * three ribbon colors and a brightness slider, applied to the running
 * scene (TubesBackground) without a reload.
 *
 * The side bar (a panel that slides in from the right edge on a desk, a
 * Sheet on a phone) lists every dashboard card with a switch, lets the
 * picked ones be reordered, and gives four color slots for the board:
 * background, card, border, accent, each with a Clear option where it makes
 * sense. Picks and colors are saved in this browser (localStorage, the same
 * way the chat draft is kept), so phone and desk each keep their own board.
 * Nothing here talks to the server; the cards render from the dashboard
 * data already loaded. */

const STORE_KEY = 'kc-milli-board';
const CLEAR = 'transparent';

export const BOARD_DEFAULTS = {
  bg: CLEAR,
  card: '#2c2e33',
  border: CLEAR,
  accent: '#C08A5E',
};

export const TUBES_DEFAULTS = { colors: DEFAULT_TUBE_COLORS, opacity: 1 };

/* Presets per slot: the rulebook's grounds and metals, plus FormQuick's purple. */
const PRESETS = {
  bg: [CLEAR, '#1e2023', '#25272B', '#0e0f11', '#2a2420', '#241f2e', '#EDEBE6'],
  card: ['#2c2e33', '#25272B', '#151618', '#3a2f28', '#2e2740', '#f4f2ee'],
  border: [CLEAR, '#3a3c42', '#4a4c52', '#C08A5E', '#C9A24B', '#7B4FC0', '#b8b8b8'],
  accent: ['#C08A5E', '#C9A24B', '#7B4FC0', '#b8b8b8', '#EDEBE6', '#5fa36b'],
};
const RIBBON_PRESETS = ['#25272B', '#b8b8b8', '#C9A24B', '#C08A5E', '#7B4FC0', '#EDEBE6', '#5fa36b', '#4f7fc0'];

const SLOTS = ['bg', 'card', 'border', 'accent'];

const isHex = (v) => typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v);
const isColor = (v) => v === CLEAR || isHex(v);

/* Relative luminance of a hex color; above 0.45 we call it light and switch
 * the text to charcoal so words stay readable on a pale ground. */
const isLight = (hex) => {
  if (!isHex(hex)) return false;
  const n = parseInt(hex.slice(1), 16);
  const ch = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const L = 0.2126 * ch((n >> 16) & 255) + 0.7152 * ch((n >> 8) & 255) + 0.0722 * ch(n & 255);
  return L > 0.45;
};

const emptyBoard = () => ({ cards: [], colors: { ...BOARD_DEFAULTS }, tubes: { colors: [...TUBES_DEFAULTS.colors], opacity: TUBES_DEFAULTS.opacity } });

/** What this browser saved for the board; defaults where nothing is saved or
 * the saved value is not usable. Exported so the dashboard can start the
 * ribbons in the saved colors before the board mounts. */
export const readBoardSettings = (knownIds = null) => {
  const out = emptyBoard();
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return out;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.cards)) out.cards = parsed.cards.filter((id) => typeof id === 'string' && (!knownIds || knownIds.includes(id)));
    // v1 boards (2026-10-24) saved a charcoal ground and a hairline border as
    // their defaults; v2 opened the board up. A v1 save that still carries
    // those two defaults reads as clear, so the open space arrives without a
    // Reset; a color the owner picked himself stays.
    const v1 = !(Number(parsed?.v) >= 2);
    SLOTS.forEach((k) => {
      const c = parsed?.colors?.[k];
      if (!isColor(c)) return;
      if (v1 && ((k === 'bg' && c === '#1e2023') || (k === 'border' && c === '#3a3c42'))) return;
      out.colors[k] = c;
    });
    if (Array.isArray(parsed?.tubes?.colors)) {
      parsed.tubes.colors.slice(0, 3).forEach((c, i) => { if (isHex(c)) out.tubes.colors[i] = c; });
    }
    const op = Number(parsed?.tubes?.opacity);
    if (Number.isFinite(op) && op >= 0.2 && op <= 1) out.tubes.opacity = op;
    return out;
  } catch {
    return out;
  }
};

const writeBoard = (board) => {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({ v: 2, cards: board.cards, colors: board.colors, tubes: board.tubes }));
  } catch { /* storage blocked or full: the board still works for this visit */ }
};

const Swatch = ({ value, selected, onPick, label }) => (
  <button
    type="button"
    onClick={onPick}
    className={`relative h-6 w-6 overflow-hidden rounded-full border-2 ${selected ? 'border-[#EDEBE6]' : 'border-[#3a3c42]'}`}
    style={value === CLEAR ? undefined : { background: value }}
    aria-label={label}
    aria-pressed={selected}
    title={label}
  >
    {value === CLEAR ? <span aria-hidden="true" className="absolute left-1/2 top-1/2 h-[2px] w-7 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-[#C08A5E]" /> : null}
  </button>
);

const Picker = ({ value, onPick, label }) => (
  <label className="relative ml-1 h-6 w-6 cursor-pointer overflow-hidden rounded-full border-2 border-dashed border-[#6a6a6a]" title={label}>
    <input
      type="color"
      value={isHex(value) ? value : '#1e2023'}
      onChange={(e) => onPick(e.target.value)}
      className="absolute -left-1 -top-1 h-9 w-9 cursor-pointer opacity-0"
      aria-label={label}
    />
    <span aria-hidden="true" className="absolute inset-0 rounded-full" style={{ background: 'conic-gradient(#C08A5E, #C9A24B, #7B4FC0, #b8b8b8, #C08A5E)' }} />
  </label>
);

export default function ConsoleBoard({ ctx, cards, tx, isDesktop, onTubesChange }) {
  const knownIds = useMemo(() => cards.map((c) => c.id), [cards]);
  const [board, setBoard] = useState(() => readBoardSettings(knownIds));
  const [open, setOpen] = useState(false);

  useEffect(() => { writeBoard(board); }, [board]);
  useEffect(() => { onTubesChange?.(board.tubes); }, [board.tubes, onTubesChange]);

  // Esc closes the side bar, like the dock.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const bx = (key, opts) => tx(`board.${key}`, opts);
  const labelOf = (card) => (card.labelKey ? tx(card.labelKey) : card.label);
  const colorName = (v) => (v === CLEAR ? bx('clear') : v);

  const toggleCard = (id, on) => setBoard((b) => ({
    ...b,
    cards: on ? (b.cards.includes(id) ? b.cards : [...b.cards, id]) : b.cards.filter((x) => x !== id),
  }));
  const moveCard = (id, dir) => setBoard((b) => {
    const i = b.cards.indexOf(id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= b.cards.length) return b;
    const next = [...b.cards];
    [next[i], next[j]] = [next[j], next[i]];
    return { ...b, cards: next };
  });
  const setColor = (slot, value) => { if (isColor(value)) setBoard((b) => ({ ...b, colors: { ...b.colors, [slot]: value } })); };
  const setRibbon = (i, value) => {
    if (!isHex(value)) return;
    setBoard((b) => { const colors = [...b.tubes.colors]; colors[i] = value; return { ...b, tubes: { ...b.tubes, colors } }; });
  };
  const setBrightness = (value) => {
    const op = Number(value);
    if (!Number.isFinite(op)) return;
    setBoard((b) => ({ ...b, tubes: { ...b.tubes, opacity: Math.max(0.2, Math.min(1, op)) } }));
  };
  const reset = () => setBoard(emptyBoard());

  const { colors } = board;
  const lightBg = isLight(colors.bg);
  const lightCard = isLight(colors.card);
  const text = lightBg ? '#1e2023' : '#EDEBE6';
  const muted = lightBg ? '#5c5852' : '#8f8b83';
  const picked = board.cards.map((id) => cards.find((c) => c.id === id)).filter(Boolean);
  const clear = colors.bg === CLEAR;

  // The board's own context: same data, its colors, its own test ids.
  const boardCtx = { ...ctx, board: true, accent: colors.accent, tid: (id) => `board-${id}` };

  const panel = (
    <div className="flex h-full flex-col text-[#EDEBE6]" data-testid="milli-board-panel">
      <div className="flex items-center justify-between border-b border-[#3a3c42] px-4 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#b0aca3]">{bx('customize')}</div>
        <button type="button" onClick={() => setOpen(false)} className="rounded-md p-1 text-[#b0aca3] hover:text-[#EDEBE6]" aria-label={bx('close')} data-testid="milli-board-close">
          <X size={16} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {isDesktop ? (
          <div className="mb-5" data-testid="milli-board-ribbons">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#b0aca3]">{bx('ribbons')}</div>
            <p className="mt-1 text-[11px] text-[#8f8b83]">{bx('ribbonsHint')}</p>
            {board.tubes.colors.map((c, i) => (
              <div key={i} className="mt-3" data-testid={`milli-board-ribbon-${i}`}>
                <div className="mb-1.5 flex items-center justify-between text-[12px]">
                  <span className="text-[#EDEBE6]">{bx('ribbon', { n: i + 1 })}</span>
                  <span className="font-mono text-[11px] text-[#8f8b83]">{c}</span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  {RIBBON_PRESETS.map((hex) => (
                    <Swatch key={hex} value={hex} selected={c.toLowerCase() === hex.toLowerCase()} onPick={() => setRibbon(i, hex)} label={`${bx('ribbon', { n: i + 1 })} ${hex}`} />
                  ))}
                  <Picker value={c} onPick={(v) => setRibbon(i, v)} label={`${bx('ribbon', { n: i + 1 })} ${bx('anyColor')}`} />
                </div>
              </div>
            ))}
            <div className="mt-3">
              <div className="mb-1.5 flex items-center justify-between text-[12px]">
                <span className="text-[#EDEBE6]">{bx('brightness')}</span>
                <span className="font-mono text-[11px] text-[#8f8b83]">{Math.round(board.tubes.opacity * 100)}%</span>
              </div>
              <input
                type="range"
                min="0.2"
                max="1"
                step="0.1"
                value={board.tubes.opacity}
                onChange={(e) => setBrightness(e.target.value)}
                className="w-full accent-[#C08A5E]"
                aria-label={bx('brightness')}
                data-testid="milli-board-brightness"
              />
            </div>
          </div>
        ) : null}

        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#b0aca3]">{bx('cards')}</div>
        <ul className="mt-2 space-y-1">
          {cards.map((card) => {
            const on = board.cards.includes(card.id);
            const pos = board.cards.indexOf(card.id);
            return (
              <li key={card.id} className="flex items-center gap-2 rounded-md px-1 py-1.5" data-testid={`milli-board-pick-${card.id}`}>
                <Switch
                  checked={on}
                  onCheckedChange={(v) => toggleCard(card.id, v)}
                  aria-label={labelOf(card)}
                  className="data-[state=checked]:bg-[#C08A5E] data-[state=unchecked]:bg-[#3a3c42]"
                />
                <span className={`min-w-0 flex-1 truncate text-[13px] ${on ? 'text-[#EDEBE6]' : 'text-[#b0aca3]'}`}>{labelOf(card)}</span>
                {on ? (
                  <span className="flex shrink-0 gap-0.5">
                    <button type="button" onClick={() => moveCard(card.id, -1)} disabled={pos === 0} className="rounded p-0.5 text-[#b0aca3] hover:text-[#EDEBE6] disabled:opacity-30" aria-label={bx('moveUp')}>
                      <ChevronUp size={14} />
                    </button>
                    <button type="button" onClick={() => moveCard(card.id, 1)} disabled={pos === board.cards.length - 1} className="rounded p-0.5 text-[#b0aca3] hover:text-[#EDEBE6] disabled:opacity-30" aria-label={bx('moveDown')}>
                      <ChevronDown size={14} />
                    </button>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>

        <div className="mt-5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#b0aca3]">{bx('colors')}</div>
        {SLOTS.map((slot) => (
          <div key={slot} className="mt-3" data-testid={`milli-board-color-${slot}`}>
            <div className="mb-1.5 flex items-center justify-between text-[12px]">
              <span className="text-[#EDEBE6]">{bx(slot)}</span>
              <span className="font-mono text-[11px] text-[#8f8b83]">{colorName(colors[slot])}</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {PRESETS[slot].map((v) => (
                <Swatch key={v} value={v} selected={colors[slot].toLowerCase() === v.toLowerCase()} onPick={() => setColor(slot, v)} label={`${bx(slot)} ${colorName(v)}`} />
              ))}
              <Picker value={colors[slot]} onPick={(v) => setColor(slot, v)} label={`${bx(slot)} ${bx('anyColor')}`} />
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-[#3a3c42] px-4 py-3">
        <button type="button" onClick={reset} className="flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-[0.12em] text-[#b0aca3] hover:text-[#EDEBE6]" data-testid="milli-board-reset">
          <RotateCcw size={13} /> {bx('reset')}
        </button>
      </div>
    </div>
  );

  return (
    <div
      className={`relative mt-6 overflow-hidden rounded-[14px] border p-4 lg:p-6 ${isDesktop ? 'min-h-[70vh]' : 'min-h-[320px]'}`}
      style={{
        background: colors.bg,
        borderColor: colors.border,
        color: text,
        '--board-card': colors.card,
        '--board-border': colors.border === CLEAR ? '#3a3c42' : colors.border,
        '--board-accent': colors.accent,
      }}
      data-testid="milli-board"
    >
      <div className="mb-4 flex items-center gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: muted }}>{bx('title')}</div>
          <div className="text-[12px]" style={{ color: muted }}>{bx('count', { n: picked.length })}</div>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ml-auto flex items-center gap-1.5 rounded-[10px] border px-3 py-2 text-[12px] font-bold uppercase tracking-[0.12em]"
          style={{ borderColor: colors.accent, color: colors.accent, background: clear ? 'rgba(30,32,35,0.6)' : undefined }}
          data-testid="milli-board-customize"
        >
          <SlidersHorizontal size={13} /> {bx('customize')}
        </button>
      </div>

      {picked.length === 0 ? (
        <p className="max-w-[520px] text-[13px]" style={{ color: muted }} data-testid="milli-board-empty">{isDesktop ? bx('emptyOpen') : bx('empty')}</p>
      ) : (
        <div className={`grid grid-cols-1 gap-3 lg:grid-cols-3 ${lightCard ? 'milli-board-light' : ''}`} data-testid="milli-board-grid">
          {picked.map((card) => (
            <div key={card.id} className={card.wide ? 'lg:col-span-2' : ''} data-testid={`milli-board-card-${card.id}`}>
              {card.render(boardCtx)}
            </div>
          ))}
        </div>
      )}

      {isDesktop ? (
        <>
          {/* Grab tab on the right edge while the panel is closed. */}
          {!open ? (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="absolute right-0 top-1/2 -translate-y-1/2 rounded-l-md border border-r-0 px-1 py-3"
              style={{ borderColor: colors.border === CLEAR ? '#3a3c42' : colors.border, background: colors.card, color: muted }}
              aria-label={bx('customize')}
              data-testid="milli-board-handle"
            >
              <ChevronLeft size={14} />
            </button>
          ) : null}
          <aside
            className={`absolute right-0 top-0 h-full w-[300px] border-l border-[#3a3c42] bg-[#1e2023] shadow-[-8px_0_24px_rgba(0,0,0,0.35)] transition-transform duration-200 ${open ? 'translate-x-0' : 'translate-x-full'}`}
            aria-hidden={!open}
            data-testid="milli-board-sidebar"
          >
            {panel}
          </aside>
        </>
      ) : (
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="right" className="w-[300px] border-[#3a3c42] bg-[#1e2023] p-0 text-[#EDEBE6] [&>button]:hidden">
            <SheetTitle className="sr-only">{bx('customize')}</SheetTitle>
            {panel}
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
